import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, X } from "lucide-react";
import { getBatches, createBatch, updateBatch, deleteBatch } from "../../lib/api";

const EMPTY = { name: "", start_date: "", total_seats: 30, seats_left: 30, mode: "Both", active: true };

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

export default function BatchesView() {
  const [batches, setBatches] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setBatches(await getBatches());
    } catch (e) { toast.error(e.message); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, total_seats: Number(form.total_seats), seats_left: Number(form.seats_left), start_date: new Date(form.start_date).toISOString() };
      if (editing) {
        await updateBatch(editing, payload);
        toast.success("Batch updated");
      } else {
        await createBatch(payload);
        toast.success("Batch created");
      }
      setShowForm(false);
      setEditing(null);
      setForm(EMPTY);
      load();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  const onEdit = (b) => {
    setEditing(b.id);
    setForm({
      name: b.name,
      start_date: new Date(b.start_date).toISOString().slice(0, 16),
      total_seats: b.total_seats,
      seats_left: b.seats_left,
      mode: b.mode,
      active: b.active,
    });
    setShowForm(true);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this batch?")) return;
    try {
      await deleteBatch(id);
      toast.success("Batch deleted");
      load();
    } catch (e) { toast.error(e.message); }
  };

  const inputCls = "w-full rounded-xl bg-[#070A08] border border-[#22C55E]/[0.18] focus:border-[#22C55E]/60 outline-none px-4 py-3 text-white text-sm placeholder:text-[#6B7A70] transition-colors";

  return (
    <div data-testid="batches-view">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading uppercase text-3xl sm:text-4xl tracking-wide text-white">Batches</h1>
          <p className="mt-0.5 text-sm text-[#6B7A70]">The active upcoming batch powers the countdown on the landing page.</p>
        </div>
        <button
          onClick={() => { setEditing(null); setForm(EMPTY); setShowForm(true); }}
          data-testid="batch-create-button"
          className="inline-flex items-center gap-2 rounded-full bg-[#22C55E] hover:bg-[#16A34A] text-[#04100A] text-xs font-extrabold px-5 py-2.5 transition-colors"
        >
          <Plus className="w-4 h-4" /> New batch
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="mb-6 rounded-2xl bg-[#0F1712] border border-[#22C55E]/30 p-5 sm:p-6" data-testid="batch-form">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading uppercase text-xl text-white tracking-wide">{editing ? "Edit Batch" : "New Batch"}</h2>
            <button type="button" onClick={() => setShowForm(false)} aria-label="Close form" data-testid="batch-form-close" className="text-[#A7B3AA] hover:text-white"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div>
              <label htmlFor="batch-name" className="block text-xs font-bold uppercase tracking-wider text-[#A7B3AA] mb-1.5">Batch name</label>
              <input id="batch-name" required value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. November Batch 2" className={inputCls} data-testid="batch-name-input" />
            </div>
            <div>
              <label htmlFor="batch-start" className="block text-xs font-bold uppercase tracking-wider text-[#A7B3AA] mb-1.5">Start date & time</label>
              <input id="batch-start" required type="datetime-local" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className={inputCls} data-testid="batch-start-input" />
            </div>
            <div>
              <label htmlFor="batch-mode" className="block text-xs font-bold uppercase tracking-wider text-[#A7B3AA] mb-1.5">Mode</label>
              <select id="batch-mode" value={form.mode} onChange={(e) => set("mode", e.target.value)} className={inputCls} data-testid="batch-mode-select">
                <option>Online</option>
                <option>Offline</option>
                <option>Both</option>
              </select>
            </div>
            <div>
              <label htmlFor="batch-total" className="block text-xs font-bold uppercase tracking-wider text-[#A7B3AA] mb-1.5">Total seats</label>
              <input id="batch-total" required type="number" min="1" value={form.total_seats} onChange={(e) => set("total_seats", e.target.value)} className={inputCls} data-testid="batch-total-input" />
            </div>
            <div>
              <label htmlFor="batch-left" className="block text-xs font-bold uppercase tracking-wider text-[#A7B3AA] mb-1.5">Seats left</label>
              <input id="batch-left" required type="number" min="0" value={form.seats_left} onChange={(e) => set("seats_left", e.target.value)} className={inputCls} data-testid="batch-left-input" />
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="batch-active-toggle">
                <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} className="w-4 h-4 accent-[#22C55E]" data-testid="batch-active-checkbox" />
                <span className="text-sm text-[#A7B3AA]">Active (shows on landing page)</span>
              </label>
            </div>
          </div>
          <button type="submit" disabled={saving} data-testid="batch-save-button" className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#22C55E] hover:bg-[#16A34A] disabled:opacity-50 text-[#04100A] font-extrabold text-sm px-6 py-3 transition-colors">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {editing ? "Save changes" : "Create batch"}
          </button>
        </form>
      )}

      {!batches ? (
        <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-20 rounded-xl bg-[#0F1712] border border-[#22C55E]/[0.08] animate-pulse" />)}</div>
      ) : batches.length === 0 ? (
        <div className="rounded-2xl border border-[#22C55E]/[0.18] bg-[#0F1712] p-12 text-center" data-testid="batches-empty">
          <p className="font-heading uppercase text-2xl text-white tracking-wide">No batches yet</p>
          <p className="mt-2 text-sm text-[#6B7A70]">Create your first batch to show the live countdown on the landing page.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3" data-testid="batches-list">
          {batches.map((b, i) => {
            const pct = b.total_seats ? Math.round((b.seats_left / b.total_seats) * 100) : 0;
            return (
              <div key={b.id} className="rounded-2xl bg-[#0F1712] border border-[#22C55E]/[0.18] p-5" data-testid={`batch-card-${i}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-heading uppercase text-2xl text-white tracking-wide">{b.name}</h3>
                    <p className="text-xs text-[#6B7A70] mt-0.5">Starts {fmt(b.start_date)} · {b.mode}</p>
                  </div>
                  <span className={`rounded-full text-[10px] font-bold px-2.5 py-1 border ${b.active ? "text-[#22C55E] border-[#22C55E]/40 bg-[#22C55E]/10" : "text-[#6B7A70] border-white/15 bg-white/5"}`} data-testid={`batch-active-badge-${i}`}>
                    {b.active ? "ACTIVE" : "OFF"}
                  </span>
                </div>
                <div className="mt-4">
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="text-[#A7B3AA]">{b.seats_left} of {b.total_seats} seats left</span>
                    <span className="font-bold text-[#FACC15]">{pct}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-[#17231B] overflow-hidden">
                    <div className="h-full bg-[#22C55E] rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  <button onClick={() => onEdit(b)} data-testid={`batch-edit-${i}`} className="inline-flex items-center gap-1.5 rounded-full border border-[#22C55E]/30 text-[#A7B3AA] hover:text-white text-xs font-bold px-3.5 py-1.5 transition-colors">
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                  <button onClick={() => onDelete(b.id)} data-testid={`batch-delete-${i}`} className="inline-flex items-center gap-1.5 rounded-full border border-[#EF4444]/30 text-[#EF4444] text-xs font-bold px-3.5 py-1.5 hover:bg-[#EF4444]/10 transition-colors">
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
